import React, { useState, useEffect, useRef } from 'react';
import { imageCache } from '../utils/imageCache';
import './CachedImage.css';

interface CachedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  fallbackSrc?: string;
}

const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  onLoad,
  onError,
  fallbackSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMjAgMTgwSDEwNFY5NkgxMjBWMTgwWk0xNjggMTgwSDE1MlY5NkgxNjhWMTgwWk0xOTYgMTgwSDE4MFY5NkgxOTZWMTgwWiIgZmlsbD0iI0RERERERCIvPgo8L3N2Zz4='
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    let isMounted = true;

    // 首先检查缓存
    const cachedImage = imageCache.getCachedImage(src);
    if (cachedImage) {
      setImageSrc(src);
      setIsLoading(false);
      setHasError(false);
      onLoad?.();
      return;
    }

    // 如果没有缓存，开始加载
    setIsLoading(true);
    setHasError(false);

    imageCache.loadImage(src)
      .then(() => {
        if (isMounted) {
          setImageSrc(src);
          setIsLoading(false);
          onLoad?.();
        }
      })
      .catch(() => {
        if (isMounted) {
          setImageSrc(fallbackSrc);
          setIsLoading(false);
          setHasError(true);
          onError?.();
        }
      });

    return () => {
      isMounted = false;
    };
  }, [src, fallbackSrc, onLoad, onError]);

  return (
    <div className={`cached-image-container ${className || ''}`}>
      {isLoading && (
        <div className="image-loading-placeholder">
          <div className="image-loading-spinner"></div>
        </div>
      )}
      {imageSrc && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          loading={loading}
          style={{
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease'
          }}
          onLoad={() => {
            setIsLoading(false);
            onLoad?.();
          }}
          onError={() => {
            if (!hasError) {
              setImageSrc(fallbackSrc);
              setHasError(true);
              onError?.();
            }
          }}
        />
      )}
    </div>
  );
};

export default CachedImage;
